import { SiteUrls } from '@tableau/shared-urls';
import { CheckBoxWidget, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { defaultMemoize } from 'reselect';

import { FlowScheduledTasksGrid, FlowScheduledTasksGridRow } from '../../app/content/FlowScheduledTasksGrid';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Formatters, Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { getHeightForRows, SortableDataGrid } from '../../common/react/components/dataGrid/SortableDataGrid';
import { ActionsMenu } from '../../common/react/components/SharedWidgetsActionMenu';
import { filePathDisplayStringWithoutBaseName } from '../../common/utilities/FilePathUtils';
import { classes, cssClass, flexColumnStyle, flexRowStyle, textEllipsis } from '../../style';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { ISliceable } from '../../ts/models/ISliceable';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { FileConnectionDetail } from '../../ts/ServerApi/types/ConnectionDetail';
import { IOrderClause } from '../../ts/ServerApi/types/IOrderClause';
import { FlowTaskActions } from '../../ts/services/FlowTaskActions';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { getConnectionText } from '../utils/FlowDataConnectionUtils';
import { getExpandIcon, getRunFlowTaskStatusIconClass } from '../utils/FlowIconUtils';
import { getRefreshTypeDisplayText } from '../utils/FlowOutputStepUtils';
import { localizeRunFlowTaskStatus } from '../utils/FlowStatusUtils';
import { FlowSuspendedIconWithTooltip } from './FlowSuspendedIconWithTooltip';

const checkboxAreaWidth = 26;
const grey203border = `1px solid ${CommonStyles.Colors.Grey203}`;

const cssPrefix = 'FlowRunScheduleItem';

const rootClass = cssClass(cssPrefix, 'root', flexColumnStyle, {
  marginBottom: CommonStyles.Sizes.LargeSpace,
  border: grey203border,
  paddingBottom: 1,
  backgroundColor: CommonStyles.Colors.White
});

const taskInfo = cssClass(cssPrefix, 'taskInfo', {
  display: 'flex',
  alignItems: 'center',
});

const checkboxWrapperClass = cssClass(cssPrefix, 'checkboxWrapper', {
  display: 'flex',
  paddingRight: CommonStyles.Sizes.DefaultSpace,
  height: '100%',
  alignItems: 'center'
});

const noCheckboxClass = cssClass(cssPrefix, 'noCheckbox', {
  width: checkboxAreaWidth
});

const selectedClass = cssClass(cssPrefix, 'selected', {
  backgroundColor: CommonStyles.Colors.Grey235
});

export const scheduleClass = cssClass(cssPrefix, 'schedule', {
  padding: `${CommonStyles.Sizes.MediumSpace}px 0 ${CommonStyles.Sizes.MediumSpace}px ${CommonStyles.Sizes.MediumSpace}px`,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  maxWidth: 400
});

const scheduleHeaderClass = cssClass(cssPrefix, 'scheduleHeader', flexRowStyle);

const scheduleLinkClass = cssClass(cssPrefix, 'scheduleLink', textEllipsis, {
  fontSize: CommonStyles.Sizes.LargeFontSize
});

const scheduleDescriptionClass = cssClass(cssPrefix, 'scheduleDescription', flexRowStyle, {
  fontWeight: 'normal',
  color: CommonStyles.Colors.Grey89,
  paddingLeft: checkboxAreaWidth
});

const suspendedTaskClass = cssClass(cssPrefix, 'suspendedTaskClass', {
  color: TabStyles.Colors.AttentionLight,
  paddingLeft: checkboxAreaWidth,
  display: 'flex',
  alignItems: 'center'
});

const actionMenuClass = cssClass(cssPrefix, 'actionMenu', {
  marginLeft: CommonStyles.Sizes.DefaultSpace,
  position: 'relative',
  bottom: CommonStyles.Sizes.SmallSpace
});

export const outputCount = cssClass(cssPrefix, 'outputCount', flexRowStyle, {
  marginLeft: 'auto',
  marginRight: CommonStyles.Sizes.MediumSpace,
  cursor: 'pointer'
});

const expandIcon = cssClass(cssPrefix, 'expandIcon', {
  width: 20,
  height: 16
});

const statusIcon = cssClass(cssPrefix, 'statusIcon', {
  flexShrink: 0,
  width: 19,
  height: 19,
  marginRight: 4
});

const statusText = cssClass(cssPrefix, 'statusText', {
  marginRight: CommonStyles.Sizes.DefaultSpace
});

export const outputDatagrid = cssClass(cssPrefix, 'outputDatagrid', {
  position: 'relative',
  marginBottom: -2,
  borderBottom: grey203border,
  paddingBottom: CommonStyles.Sizes.LargeSpace,
});

const actionButtonStyle: React.CSSProperties = {
  padding: `0 ${CommonStyles.Sizes.MediumSpace}px`,
  minWidth: 0
};

function getRowData(flowTask: RunFlowTaskInfo, flowOutputSteps: ReadonlyArray<FlowOutputStepInfo>): (orderClause: IOrderClause[]) => ISliceable<FlowScheduledTasksGridRow> {
  const outputRows: FlowScheduledTasksGridRow[] = flowTask.flowOutputSteps.map((taskOutput) => {
    const outputInfo = flowOutputSteps.find((flowOutput) => taskOutput.id === flowOutput.id);
    const taskOutputInfo = flowTask.flowOutputSteps.find((flowOutput) => taskOutput.id === flowOutput.id);
    const filePath: string = outputInfo && filePathDisplayStringWithoutBaseName((outputInfo.dataConnection.connectionDetails as FileConnectionDetail).fileName);
    const isIncremental = taskOutputInfo && taskOutputInfo.isIncremental;
    const outputOperationType = isIncremental ? taskOutputInfo.incrementalOutputOperationType : taskOutputInfo.outputOperationType;
    return outputInfo ? {
      outputStepName: outputInfo.name,
      outputName: outputInfo.datasourceName,
      outputType: outputInfo.dataConnection.connectionTypeDisplayName,
      outputLocation: filePath ? filePath : getConnectionText(outputInfo.dataConnection),
      outputRefreshType: getRefreshTypeDisplayText(isIncremental, outputOperationType)
    } : null;
  });
  return (orderClause: IOrderClause[]) => SliceableHelper.fromArray(outputRows, ServiceLocator.get<ng.IQService>('$q'));
}

export type FlowRunScheduleItemProps = {
  readonly anyActionsAllowed: boolean;
  readonly isServerOrSiteAdmin: boolean;
  readonly flowTask: RunFlowTaskInfo;
  readonly outputSteps: ReadonlyArray<FlowOutputStepInfo>;
  readonly selected: boolean;
  readonly flowFailureThresholdForRunPrevention: number;
  readonly handleClick: (task: RunFlowTaskInfo, selected: boolean) => void;
  readonly onUpdateTask: () => void;
  readonly handleCollapse: (id: string) => void;
  readonly collapsed: boolean;
};

export class FlowRunScheduleItem extends React.PureComponent<FlowRunScheduleItemProps> {

  private readonly memoizedCreateActionsMenuItems = defaultMemoize(createActionsMenuItems);
  private readonly memoizedGetRowData = defaultMemoize(getRowData);

  private readonly descriptionFilter: IScheduleFrequencyDescriptionFilter;
  private readonly siteUrls: SiteUrls;

  constructor(props: FlowRunScheduleItemProps) {
    super(props);
    this.descriptionFilter = ServiceLocator.get<IScheduleFrequencyDescriptionFilter>('scheduleFrequencyDescriptionFilter');
    this.siteUrls = new SiteUrls({
      siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName(),
      queryString: window.location.search
    });
    this.state = {
      expanded: true
    };
  }

  private readonly toggleItemCollapse = () => {
    this.props.handleCollapse(this.props.flowTask.id);
  }

  private readonly handleChange = () => {
    this.props.handleClick(this.props.flowTask, this.props.selected);
  }

  private renderCheckbox(): JSX.Element | null {
    if (this.props.anyActionsAllowed) {
      return (
        <div className={checkboxWrapperClass}>
          <CheckBoxWidget
            checked={this.props.selected}
            handleChange={this.handleChange}
            testId={'flow-run-schedule-item-' + this.props.flowTask.id}
            removeLeftMargin={true}
          />
        </div>
      );
    } else {
      return <div className={noCheckboxClass}/>;
    }
  }

  private renderScheduleText(): JSX.Element {
    return this.props.isServerOrSiteAdmin ?
      <a className={scheduleLinkClass}
        href={this.siteUrls.scheduleFlowsUrl(this.props.flowTask.schedule.id)}
        title={this.props.flowTask.schedule.name}
        data-tb-test-id={'flow-run-schedule-item-link'}
      >
        {this.props.flowTask.schedule.name}
      </a>
    :
      <div
        className={scheduleLinkClass}
        title={this.props.flowTask.schedule.name}
        data-tb-test-id={'flow-run-schedule-item-link'}
      >
        {this.props.flowTask.schedule.name}
      </div>;
  }

  private renderSchedule(): JSX.Element {
    return (
      <div className={scheduleClass}>
        <div className={scheduleHeaderClass}>
          {this.renderCheckbox()}
          {this.renderScheduleText()}
          {this.renderActionsMenu()}
        </div>
        <div className={scheduleDescriptionClass}>
          {this.descriptionFilter(this.props.flowTask.schedule)}
        </div>
        {this.renderStatusAndNextRun()}
      </div>
    );
  }

  private renderStatusAndNextRun(): JSX.Element {
    return FeatureFlags.autoSuspendFlowTasksEnabled && this.props.flowTask.suspended ? (
      <div className={suspendedTaskClass}>
        <FlowSuspendedIconWithTooltip
          flowFailureThresholdForRunPrevention={this.props.flowFailureThresholdForRunPrevention}
        />
        {Messages.flowTasksSuspended()}
      </div>)
    : this.props.flowTask.runNextAt ? (
      <div className={scheduleDescriptionClass}>
        {
          FeatureFlags.autoSuspendFlowTasksEnabled ?
            <React.Fragment>
              <div className={classes(getRunFlowTaskStatusIconClass(this.props.flowTask.status), statusIcon)}/>
              <div className={statusText}>{localizeRunFlowTaskStatus(this.props.flowTask.status)}</div>
            </React.Fragment> : null
        }
        {Messages.nextRunAt({NEXT_RUN:Formatters.formatMediumDateTimeWithoutSeconds(this.props.flowTask.runNextAt)})}
      </div>
    ) : null;
  }

  private renderActionsMenu(): JSX.Element | null {
    if (this.props.anyActionsAllowed) {
      const actions = this.memoizedCreateActionsMenuItems(this.props.flowTask, this.props.onUpdateTask);
      return (
        <div className={actionMenuClass}>
          <ActionsMenu
            actions={actions}
            buttonLabel={'\u2022\u2022\u2022'}
            style={actionButtonStyle}
          />
        </div>
      );
    } else {
      return null;
    }
  }

  private renderOutputCountAndExpando(): JSX.Element {
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return (
      <div className={outputCount} onClick={this.toggleItemCollapse}>
        {this.props.flowTask.runAllOutputs ? Messages.outputStepsAll() : Messages.outputsCount({COUNT: this.props.flowTask.flowOutputSteps.length, COUNT_LOC: Formatters.formatCount(this.props.flowTask.flowOutputSteps.length)})}
        <div className={classes(expandIcon, getExpandIcon(!this.props.collapsed))}/>
      </div>
    );
  }

  private renderTaskInfo(): JSX.Element {
    return (
      <div className={taskInfo}>
        {this.renderSchedule()}
        {this.renderOutputCountAndExpando()}
      </div>
    );
  }

  private renderOutputDatagrid(): JSX.Element {
    const columns = FlowScheduledTasksGrid.columns();
    const sliceable = this.memoizedGetRowData(
      this.props.flowTask,
      this.props.outputSteps
    );
    return (
      <div className={outputDatagrid} style={{ height: getHeightForRows(this.props.flowTask.flowOutputSteps.length + 1)}}>
        <SortableDataGrid
          columns={columns}
          getSliceable={sliceable}
        />
      </div>
    );
  }

  public render(): JSX.Element {
    return (
      <div className={this.props.selected ? classes(selectedClass, rootClass) : rootClass}>
        {this.renderTaskInfo()}
        {this.props.collapsed ? null : this.renderOutputDatagrid()}
      </div>
    );
  }
}

function createActionsMenuItems(flowTask: RunFlowTaskInfo, done: () => void) {
  return FlowTaskActions.getFlowTasksActions(
    { [flowTask.id]: flowTask },
    done
  );
}
